<template>
  <div class="age-picker-inline">
    <div class="age-picker-inline__header">
      <span class="age-picker-inline__label">Descritores de Conteúdo</span>
      <v-chip
        v-if="resultRating"
        x-small
        :color="ratingColor(resultRating.age)"
        :dark="resultRating.age !== 12"
        :light="resultRating.age === 12"
        label
        class="font-weight-bold"
      >
        {{ resultRating.rating }}
      </v-chip>
      <v-chip v-else x-small color="green darken-1" dark label>Livre</v-chip>
    </div>
    <div class="age-picker-inline__hint">
      Marque os elementos de conteúdo presentes nesta obra. A classificação mais alta será aplicada automaticamente.
    </div>

    <div class="age-picker-inline__list">
      <!-- Opção Livre -->
      <div
        class="age-picker-inline__item"
        :class="{ 'age-picker-inline__item--active': freeSelected }"
        @click="selectFree"
      >
        <v-icon
          class="age-picker-inline__check"
          :color="freeSelected ? 'var(--zinnes-primary)' : 'grey darken-1'"
          small
        >
          {{ freeSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
        </v-icon>
        <span class="age-picker-inline__desc">Livre para todos os públicos</span>
        <v-chip x-small color="green darken-1" dark label class="age-picker-inline__badge ml-auto">
          Livre
        </v-chip>
      </div>

      <v-divider style="border-color: rgba(0,0,0,0.08);"></v-divider>

      <div
        v-for="item in ageRatings"
        :key="item.id"
        class="age-picker-inline__item"
        :class="{ 'age-picker-inline__item--active': isSelected(item.id) }"
        @click="toggleItem(item.id)"
      >
        <v-icon
          class="age-picker-inline__check"
          :color="isSelected(item.id) ? 'var(--zinnes-primary)' : 'grey darken-1'"
          small
        >
          {{ isSelected(item.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
        </v-icon>
        <span class="age-picker-inline__desc">{{ item.description }}</span>
        <v-chip
          x-small
          :color="ratingColor(item.age)"
          :dark="item.age !== 12"
          :light="item.age === 12"
          label
          class="age-picker-inline__badge ml-auto"
        >
          {{ item.rating }}
        </v-chip>
      </div>
    </div>

    <div class="age-picker-inline__warning">
      <v-icon x-small color="red lighten-2">mdi-alert-circle-outline</v-icon>
      <span>Conteúdo +18 não é permitido na plataforma.</span>
    </div>
  </div>
</template>

<script>
import { getHighestAgeRating, normalizeAgeRatingIds } from '@/utils/ageRatings';

export default {
  name: 'AgeRatingPickerInline',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [Array, Number, String, Object],
      default: () => [],
    },
    ageRatings: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedIds: [],
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        this.selectedIds = normalizeAgeRatingIds(val);
      },
    },
  },
  computed: {
    freeSelected() {
      return this.selectedIds.length === 0;
    },
    resultRating() {
      return getHighestAgeRating(this.ageRatings, this.selectedIds);
    },
  },
  methods: {
    selectFree() {
      this.selectedIds = [];
      this.$emit('input', []);
    },
    toggleItem(id) {
      const normalizedId = normalizeAgeRatingIds(id)[0];
      if (normalizedId === undefined) return;

      const nextSelectedIds = [...this.selectedIds];
      const idx = nextSelectedIds.indexOf(normalizedId);
      if (idx === -1) {
        nextSelectedIds.push(normalizedId);
      } else {
        nextSelectedIds.splice(idx, 1);
      }
      this.selectedIds = nextSelectedIds;
      this.$emit('input', [...nextSelectedIds]);
    },
    isSelected(id) {
      const normalizedId = normalizeAgeRatingIds(id)[0];
      return normalizedId !== undefined && this.selectedIds.includes(normalizedId);
    },
    ratingColor(age) {
      if (age <= 10) return '#0095DA';
      if (age <= 12) return '#FBC115';
      if (age <= 14) return '#F58220';
      return '#EC1D25';
    },
  },
};
</script>

<style scoped>
.age-picker-inline {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  overflow: hidden;
  background: #dadada;
}

.age-picker-inline__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 4px;
}

.age-picker-inline__label {
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  flex: 1;
}

.age-picker-inline__hint {
  font-size: 11px;
  color: #9e9e9e;
  padding: 0 14px 8px;
  line-height: 1.5;
}

.age-picker-inline__list {
  max-height: 260px;
  overflow-y: auto;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.age-picker-inline__item {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background 0.12s;
  user-select: none;
}

.age-picker-inline__item:last-child {
  border-bottom: none;
}

.age-picker-inline__item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.age-picker-inline__item--active {
  background: rgba(107, 33, 168, 0.08);
}

.age-picker-inline__item--active:hover {
  background: rgba(107, 33, 168, 0.13);
}

.age-picker-inline__check {
  flex-shrink: 0;
  margin-right: 10px;
}

.age-picker-inline__desc {
  font-size: 13px;
  color: #424242;
  flex: 1;
}

.age-picker-inline__badge {
  font-size: 10px !important;
  font-weight: 700 !important;
  flex-shrink: 0;
}

.age-picker-inline__warning {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  font-size: 11px;
  color: #e57373;
}
</style>
